import { App, message, Skeleton, Spin, Alert } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FormValidationProvider } from '../../hooks';
import { useBtlApplicationInProgressById, useGetAffordabilityCheck, useRunAffordabilityCheck } from '../../queries';
import type { MessageKeys } from '../../types/intl';
import { TypedFormatMessageFn } from '../../types/intl';
import { FormFooter } from './BtlBaseForm';
import { AffordabilityCheck } from './common/AffordabilityCheck';
import { BtlApplicationFormPageEnum } from './types';
import { useBtlFormMappedRoutes } from './useBtlFormMappedRoutes';
import { Suspense } from 'react';
import { btlApplicationFormApi } from '../../api/btlApplicationForm';
import { ITEM_TITLE_KEYS, isNestedFormSection, resolveCaseSidebarItemId } from 'components/CaseSidebar';
import { BtlCaseSidebar } from './BtlCaseSidebar';

export const BtlApplicationFormPage = () => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { modal } = App.useApp();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const formSectionParam = searchParams.get('section');
  const formSection = formSectionParam as BtlApplicationFormPageEnum;
  const { id = '' } = useParams();
  const { isLoading, data } = useBtlApplicationInProgressById(id);
  const { data: dataRuns } = useGetAffordabilityCheck(id);
  const { refetch: refetchAffordability } = useRunAffordabilityCheck(id);

  const affordabilityData = dataRuns?.[dataRuns?.length - 1 || 0]?.buyToLetOutput;

  const { FormPage, showStandaloneFormFooter, previousSection } = useBtlFormMappedRoutes(data);

  const resolvedCaseSidebarItemId = resolveCaseSidebarItemId(formSectionParam);
  const formSectionLabelId: MessageKeys = isNestedFormSection(formSectionParam)
    ? (`btlApplicationForm.${formSection}.label` as MessageKeys)
    : resolvedCaseSidebarItemId
    ? ITEM_TITLE_KEYS[resolvedCaseSidebarItemId]
    : (`btlApplicationForm.${formSection}.label` as MessageKeys);

  const disableTopSlicing = async () => {
    modal.confirm({
      title: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.title' }),
      content: (
        <FormattedMessage
          id={'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.content'}
          values={{ br: <br /> }}
        />
      ),
      width: 600,
      okText: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.confirmText' }),
      onOk: async () => {
        await btlApplicationFormApi.toggleTopSlicing(id, false);
        await refetchAffordability();
        if (
          [BtlApplicationFormPageEnum.TOP_SLICING, BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD].includes(
            formSection,
          ) &&
          previousSection
        ) {
          navigate({ search: `?section=${previousSection}` });
        }
        message.success(formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.successMessage' }));
      },
    });
  };

  return (
    <div className={'-mt-16 flex h-full min-h-0 bg-gray-100'}>
      <div className='flex h-full min-h-0 shrink-0'>
        <BtlCaseSidebar
          data={data}
          isLoading={isLoading}
          isAffordabilityPassed={affordabilityData?.isPassed}
          isAffordabilityRunRequired={data?.isAffordabilityRunRequired}
          currentSection={formSectionParam ?? undefined}
          onDisableTopSlicing={disableTopSlicing}
        />
      </div>
      <div className='flex min-h-0 flex-1 gap-8 p-6 pb-4 pl-8'>
        <div className={'flex grow flex-col h-full relative pt-4'}>
          {(formSection === BtlApplicationFormPageEnum.SUBMIT_DIP ||
            formSection === BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION) &&
            data?.isAffordabilityRunRequired && (
              <Alert
                className='mb-4 font-semibold'
                type='warning'
                showIcon
                description={formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.invalidated.label' })}
              />
            )}
          {formSection && (
            <h2 className='mt-0'>
              <Skeleton loading={isLoading} paragraph={{ rows: 1 }} title={false} active className={'-mb-3'}>
                <FormattedMessage id={formSectionLabelId} values={{ title: data?.title }} />
              </Skeleton>
            </h2>
          )}
          <FormValidationProvider>
            <div id='form-scroll-container' className='mb-16 grow overflow-auto pb-4 pl-2 pr-4 pt-2'>
              {FormPage ? (
                <Suspense fallback={<Spin size='large' className='flex justify-center py-16' />}>
                  <FormPage />
                </Suspense>
              ) : null}
            </div>
            {showStandaloneFormFooter && <FormFooter />}
          </FormValidationProvider>
        </div>
        <div className={'w-[300px] shrink-0 bg-gradient-to-b from-white to-gray-100 rounded-xl p-4'}>
          <h3 className={'mt-0'}>{formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.label' })}</h3>
          <AffordabilityCheck showTopSlicingEligibility={true} />
        </div>
      </div>
    </div>
  );
};
